import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import ModalPortal from '../components/ModalPortal';
import { Package, CheckCircle, Truck, X, Clock, MessageSquare, Send, Image, ThumbsUp, ThumbsDown, Download, MapPin } from 'lucide-react';

const BoutiqueCommandesPage = () => {
  const { api, isAdmin } = useAuth();
  const { t, tError, formatCurrency, formatDate } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderNotes, setOrderNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [sendingNote, setSendingNote] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [savingTracking, setSavingTracking] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await api.get('/boutique/commandes');
      setOrders(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await api.put(`/boutique/commandes/${orderId}/status?status=${status}`);
      fetchOrders();
    } catch (error) {
      alert(t('boutiqueCmd.errUpdate'));
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === orders.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(orders.map(order => order.id));
    }
  };

  const toggleSelect = (orderId) => {
    if (selectedIds.includes(orderId)) {
      setSelectedIds(selectedIds.filter(id => id !== orderId));
    } else {
      setSelectedIds([...selectedIds, orderId]);
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(t('boutiqueCmd.confirmDelete', { count: selectedIds.length }))) return;
    try {
      await api.post('/boutique/commandes/bulk-delete', { order_ids: selectedIds });
      setSelectedIds([]);
      fetchOrders();
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('boutiqueCmd.errDelete'));
    }
  };

  const openNotesModal = async (order) => {
    setSelectedOrder(order);
    setShowNotesModal(true);
    await fetchOrderNotes(order.id);
  };

  const fetchOrderNotes = async (orderId) => {
    try {
      const response = await api.get(`/boutique/commandes/${orderId}/notes`);
      setOrderNotes(response.data);
    } catch (error) {
      console.error('Error fetching notes:', error);
      setOrderNotes([]);
    }
  };

  const handleSendNote = async () => {
    if (!newNote.trim() || !selectedOrder) return;
    setSendingNote(true);
    try {
      await api.post(`/boutique/commandes/${selectedOrder.id}/notes`, {
        message: newNote.trim()
      });
      setNewNote('');
      await fetchOrderNotes(selectedOrder.id);
      alert(t('boutiqueCmd.noteSent'));
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('boutiqueCmd.errSendNote'));
    } finally {
      setSendingNote(false);
    }
  };

  const openTrackingModal = (order) => {
    setSelectedOrder(order);
    setTrackingNumber(order.tracking_number || '');
    setShowTrackingModal(true);
  };

  const saveTrackingNumber = async () => {
    if (!selectedOrder) return;
    setSavingTracking(true);
    try {
      const response = await api.put(`/boutique/commandes/${selectedOrder.id}/tracking`, {
        tracking_number: trackingNumber.trim()
      });
      await fetchOrders();
      setShowTrackingModal(false);

      if (response.data.email_sent) {
        alert(t('boutiqueCmd.trackingSavedEmail'));
      } else {
        alert(t('boutiqueCmd.trackingSaved'));
      }
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('boutiqueCmd.errSaveTracking'));
    } finally {
      setSavingTracking(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'en_attente': { class: 'badge-en-cours', labelKey: 'boutiqueCmd.statutEnAttente' },
      'proof_uploaded': { class: 'badge-attente', labelKey: 'boutiqueCmd.statutProofUploaded' },
      'payee': { class: 'badge-recu', labelKey: 'boutiqueCmd.statutPayee' },
      'confirmee': { class: 'badge-recu', labelKey: 'boutiqueCmd.statutConfirmee' },
      'preparee': { class: 'badge-attente', labelKey: 'boutiqueCmd.statutPreparee' },
      'expediee': { class: 'badge-termine', labelKey: 'boutiqueCmd.statutExpediee' },
      'livree': { class: 'badge-livre', labelKey: 'boutiqueCmd.statutLivree' },
      'annulee': { class: 'badge-livre', labelKey: 'boutiqueCmd.statutAnnulee' }
    };
    const badge = badges[status] || { class: 'badge-recu', labelKey: null };
    return <span className={`badge-status ${badge.class}`}>{badge.labelKey ? t(badge.labelKey) : status}</span>;
  };

  const handleValidateProof = async (orderId) => {
    if (!window.confirm(t('boutiqueCmd.confirmValidateProof'))) return;
    try {
      await api.post(`/boutique/commandes/${orderId}/validate-proof`);
      fetchOrders();
    } catch (err) {
      alert(tError(err.response?.data?.detail) || t('boutiqueCmd.errGeneric'));
    }
  };

  const handleRejectProof = async (orderId) => {
    if (!window.confirm(t('boutiqueCmd.confirmRejectProof'))) return;
    try {
      await api.post(`/boutique/commandes/${orderId}/reject-proof`);
      fetchOrders();
    } catch (err) {
      alert(tError(err.response?.data?.detail) || t('boutiqueCmd.errGeneric'));
    }
  };

  const viewProof = async (orderId, fileId) => {
    try {
      const response = await api.get(`/boutique/commandes/${orderId}/payment-proof/${fileId}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/jpeg' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch (err) {
      alert(t('boutiqueCmd.errDownload'));
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto fade-in">
        <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 mb-6">
          🛒 {t('boutiqueCmd.pageTitle')}
        </h2>

        <div className="glass overflow-hidden">
          {isAdmin && (
            <div className="flex justify-end p-4 border-b border-white/10">
              <button
                onClick={handleDeleteSelected}
                className="px-4 py-2 rounded-xl bg-red-500/20 text-red-300 hover:bg-red-500/30 text-sm font-semibold"
                disabled={!selectedIds.length}
                data-testid="boutique-orders-delete-selected"
              >
                🗑️ {t('boutiqueCmd.deleteSelection', { count: selectedIds.length })}
              </button>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full table-dark-custom text-sm">
              <thead>
                <tr>
                  {isAdmin && (
                    <th className="w-10">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === orders.length && orders.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded"
                        data-testid="boutique-orders-select-all"
                      />
                    </th>
                  )}
                  <th>{t('boutiqueCmd.colReference')}</th>
                  <th>{t('boutiqueCmd.colClient')}</th>
                  <th>{t('boutiqueCmd.colArticles')}</th>
                  <th>{t('boutiqueCmd.colTotal')}</th>
                  <th>{t('boutiqueCmd.colAdresse')}</th>
                  <th>{t('boutiqueCmd.colStatut')}</th>
                  <th>{t('boutiqueCmd.colSuivi')}</th>
                  <th>{t('boutiqueCmd.colPaiement')}</th>
                  <th>{t('boutiqueCmd.colDate')}</th>
                  <th>{t('boutiqueCmd.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={isAdmin ? 11 : 10} className="text-center py-8">
                      <div className="loading-spinner mx-auto"></div>
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan={isAdmin ? 11 : 10} className="text-center py-8 text-gray-400">
                      {t('boutiqueCmd.noOrders')}
                    </td>
                  </tr>
                ) : (
                  orders.map((order) => (
                    <tr key={order.id}>
                      {isAdmin && (
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(order.id)}
                            onChange={() => toggleSelect(order.id)}
                            className="w-4 h-4 rounded"
                            data-testid={`boutique-order-select-${order.id}`}
                          />
                        </td>
                      )}
                      <td className="font-mono text-xs text-cyan-400">{order.ref_commande}</td>
                      <td>
                        <div>{order.client_nom}</div>
                        <div className="text-xs text-gray-400">{order.client_tel}</div>
                      </td>
                      <td>
                        {order.items?.map((item, idx) => (
                          <div key={idx} className="text-xs">
                            {item.designation} x{item.quantite}
                          </div>
                        ))}
                      </td>
                      <td className="font-bold text-green-400">{order.total != null ? formatCurrency(order.total) : ''}</td>
                      <td className="text-xs text-gray-400 max-w-[150px] truncate">
                        {typeof order.adresse_livraison === 'string'
                          ? order.adresse_livraison
                          : order.adresse_livraison?.label}
                      </td>
                      <td>{getStatusBadge(order.statut)}</td>
                      <td>
                        {order.tracking_number ? (
                          <div className="flex items-center gap-1">
                            <Truck className="w-3.5 h-3.5 text-green-400" />
                            <span className="text-xs text-green-400 font-mono">{order.tracking_number}</span>
                            <button
                              onClick={() => openTrackingModal(order)}
                              className="p-1 rounded bg-white/10 hover:bg-white/20 ml-1"
                              title={t('boutiqueCmd.editTracking')}
                            >
                              <MapPin className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => openTrackingModal(order)}
                            className="px-2 py-1 rounded-lg bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 text-xs"
                            title={t('boutiqueCmd.addTrackingTitle')}
                            data-testid={`add-tracking-${order.id}`}
                          >
                            {t('boutiqueCmd.addTrackingBtn')}
                          </button>
                        )}
                      </td>
                      <td>
                        {order.payment_proof ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => viewProof(order.id, order.payment_proof.file_id)}
                              className="p-1.5 rounded-lg bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30"
                              title={t('boutiqueCmd.viewProof')}
                              data-testid={`view-proof-${order.id}`}
                            >
                              <Image className="w-3.5 h-3.5" />
                            </button>
                            {!order.payment_proof.validated && (
                              <>
                                <button
                                  onClick={() => handleValidateProof(order.id)}
                                  className="p-1.5 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30"
                                  title={t('boutiqueCmd.validatePayment')}
                                  data-testid={`validate-proof-${order.id}`}
                                >
                                  <ThumbsUp className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleRejectProof(order.id)}
                                  className="p-1.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30"
                                  title={t('boutiqueCmd.rejectProof')}
                                  data-testid={`reject-proof-${order.id}`}
                                >
                                  <ThumbsDown className="w-3.5 h-3.5" />
                                </button>
                              </>
                            )}
                            {order.payment_proof.validated && (
                              <span className="text-xs text-green-400 font-bold">{t('boutiqueCmd.validated')}</span>
                            )}
                          </div>
                        ) : order.statut === 'payee' ? (
                          <span className="text-xs text-green-400 font-bold">SumUp</span>
                        ) : (
                          <span className="text-xs text-gray-500">-</span>
                        )}
                      </td>
                      <td className="text-gray-400">
                        {formatDate(order.date_commande)}
                      </td>
                      <td>
                        <div className="flex gap-2">
                          <button
                            onClick={() => openNotesModal(order)}
                            className="p-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors"
                            title={t('boutiqueCmd.notesMessages')}
                            data-testid={`order-notes-${order.id}`}
                          >
                            <MessageSquare className="w-4 h-4" />
                          </button>
                          <select
                            value={order.statut}
                            onChange={(e) => updateStatus(order.id, e.target.value)}
                            className="input-dark py-1 px-2 text-xs"
                          >
                            <option value="en_attente">{t('boutiqueCmd.statutEnAttente')}</option>
                            <option value="proof_uploaded">{t('boutiqueCmd.statutProofUploaded')}</option>
                            <option value="payee">{t('boutiqueCmd.statutPayee')}</option>
                            <option value="confirmee">{t('boutiqueCmd.statutConfirmee')}</option>
                            <option value="preparee">{t('boutiqueCmd.statutPreparee')}</option>
                            <option value="expediee">{t('boutiqueCmd.statutExpediee')}</option>
                            <option value="livree">{t('boutiqueCmd.statutLivree')}</option>
                            <option value="annulee">{t('boutiqueCmd.statutAnnulee')}</option>
                          </select>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Notes Modal */}
        {showNotesModal && selectedOrder && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", zIndex: 9999}}>
            <div className="modal-dark w-full max-w-2xl max-h-[80vh] flex flex-col">
              <div className="modal-dark-header flex items-center justify-between">
                <h3 className="font-bold text-lg">
                  💬 {t('boutiqueCmd.notesModalTitle', { ref: selectedOrder.ref_commande })}
                </h3>
                <button
                  onClick={() => {
                    setShowNotesModal(false);
                    setSelectedOrder(null);
                    setOrderNotes([]);
                    setNewNote('');
                  }}
                  className="text-gray-400 hover:text-white"
                  data-testid="close-notes-modal"
                >
                  ✕
                </button>
              </div>

              <div className="p-6 flex-1 overflow-y-auto">
                {/* Order Info */}
                <div className="glass p-4 rounded-xl mb-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-400">{t('boutiqueCmd.clientLabel')}</span>
                      <span className="ml-2 font-semibold text-white">{selectedOrder.client_nom}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">{t('boutiqueCmd.phoneLabel')}</span>
                      <span className="ml-2 font-semibold text-white">{selectedOrder.client_tel}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-400">{t('boutiqueCmd.deliveryAddressLabel')}</span>
                      <div className="mt-1 text-white">
                        {typeof selectedOrder.adresse_livraison === 'string'
                          ? selectedOrder.adresse_livraison
                          : selectedOrder.adresse_livraison?.label || t('boutiqueCmd.notSpecified')}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes History */}
                <div className="space-y-3 mb-4">
                  <h4 className="text-sm font-semibold text-gray-300">{t('boutiqueCmd.exchangeHistory')}</h4>
                  {orderNotes.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      {t('boutiqueCmd.noNote')}
                    </div>
                  ) : (
                    orderNotes.map((note, index) => (
                      <div
                        key={index}
                        className={`p-3 rounded-xl ${
                          note.from_staff
                            ? 'bg-blue-500/10 border border-blue-500/30 ml-8'
                            : 'bg-green-500/10 border border-green-500/30 mr-8'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-semibold text-gray-300">
                            {note.from_staff ? t('boutiqueCmd.staff') : t('boutiqueCmd.client')}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDate(note.created_at, { dateStyle: 'short', timeStyle: 'short' })}
                          </span>
                        </div>
                        <div className="text-sm text-gray-200">{note.message}</div>
                        {note.response && (
                          <div className="mt-2 pt-2 border-t border-white/10">
                            <div className="text-xs text-gray-400 mb-1">{t('boutiqueCmd.clientResponse')}</div>
                            <div className="text-sm text-green-300">{note.response}</div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>

                {/* New Note Input */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-300">{t('boutiqueCmd.sendMessageTitle')}</h4>
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder={t('boutiqueCmd.notePlaceholder')}
                    className="input-dark w-full py-3 px-4 min-h-[100px] resize-none"
                    data-testid="new-note-input"
                  />
                  <div className="text-xs text-gray-500">
                    {t('boutiqueCmd.noteHint')}
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-white/10 flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowNotesModal(false);
                    setSelectedOrder(null);
                    setOrderNotes([]);
                    setNewNote('');
                  }}
                  className="px-6 py-3 rounded-xl bg-gray-700 hover:bg-gray-600 font-bold transition-colors"
                  data-testid="cancel-note"
                >
                  {t('boutiqueCmd.close')}
                </button>
                <button
                  onClick={handleSendNote}
                  disabled={sendingNote || !newNote.trim()}
                  className="px-6 py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-bold transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="send-note-btn"
                >
                  <Send className="w-5 h-5" />
                  {sendingNote ? t('boutiqueCmd.sending') : t('boutiqueCmd.sendToClient')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tracking Modal */}
        {showTrackingModal && selectedOrder && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", zIndex: 9999}}>
            <div className="modal-dark w-full max-w-md">
              <div className="modal-dark-header flex items-center justify-between">
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <Truck className="w-5 h-5 text-orange-400" />
                  {t('boutiqueCmd.trackingNumber')}
                </h3>
                <button
                  onClick={() => {
                    setShowTrackingModal(false);
                    setSelectedOrder(null);
                    setTrackingNumber('');
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="text-sm text-gray-400">
                  {t('boutiqueCmd.orderShortLabel')} <span className="text-cyan-400 font-mono">{selectedOrder.ref_commande}</span>
                </div>
                <div className="text-sm text-gray-400">
                  {t('boutiqueCmd.clientShortLabel')} <span className="text-white font-semibold">{selectedOrder.client_nom}</span>
                </div>

                <div>
                  <label className="block text-sm text-gray-300 mb-2">{t('boutiqueCmd.trackingNumber')}</label>
                  <input
                    type="text"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder={t('boutiqueCmd.trackingPlaceholder')}
                    className="input-dark w-full py-3"
                    data-testid="tracking-input"
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {t('boutiqueCmd.trackingHint')}
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-white/10 flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowTrackingModal(false);
                    setSelectedOrder(null);
                    setTrackingNumber('');
                  }}
                  className="px-6 py-3 rounded-xl bg-gray-700 hover:bg-gray-600 font-bold transition-colors"
                >
                  {t('boutiqueCmd.cancel')}
                </button>
                <button
                  onClick={saveTrackingNumber}
                  disabled={savingTracking}
                  className="px-6 py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold transition-colors flex items-center gap-2 disabled:opacity-50"
                  data-testid="save-tracking-btn"
                >
                  <Truck className="w-5 h-5" />
                  {savingTracking ? t('boutiqueCmd.saving') : t('boutiqueCmd.save')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BoutiqueCommandesPage;
